import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Switch,
  Tooltip,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import { Add, Delete, Visibility, AccountBalance, TrendingUp, TrendingDown, Edit, FlightTakeoff, ShowChart, Close as CloseIcon, Share as ShareIcon, Gavel as RulesIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMobileDetect } from '../hooks/useMobileDetect';
import { MobilePortfolioSummary, MobilePositionCard } from '../components/mobile';
import { Portfolio, Position, UserProfile } from '../types';
import { PortfolioService } from '../services/portfolioService';
import { PositionService } from '../services/positionService';
import { AutopilotService } from '../services/autopilotService';
import { SharedContentService } from '../services/sharedContentService';
import { useUser } from '../contexts/UserContext';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { format, differenceInDays, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import CreatePortfolioDialog from '../components/CreatePortfolioDialog';
import ExecutePendingPositionDialog from '../components/ExecutePendingPositionDialog';
import ClosePositionDialog from '../components/ClosePositionDialog';
import EditPositionDialog from '../components/EditPositionDialog';
import EditPortfolioDialog from '../components/EditPortfolioDialog';
import PerformanceChartDialog from '../components/PerformanceChart';
import { StockChart } from '../components/StockChart';
import { useStockHistory } from '../hooks/useStockHistory';
import CircularProgress from '@mui/material/CircularProgress';
import { Timeline } from '@mui/icons-material';

const PortfolioList: React.FC = () => {
  const { user, profile: userProfile } = useUser();
  const navigate = useNavigate();
  const { isMobile } = useMobileDetect();
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [positions, setPositions] = useState<Record<string, Position[]>>({});
  const [pendingPositions, setPendingPositions] = useState<Record<string, Position[]>>({});
  const [closedPositions, setClosedPositions] = useState<Record<string, Position[]>>({});
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; portfolio: Portfolio | null }>({
    open: false,
    portfolio: null,
  });
  const [selectedPortfolio, setSelectedPortfolio] = useState<Portfolio | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [mobileSelectedPortfolioId, setMobileSelectedPortfolioId] = useState<string | null>(null);
  const [executePendingDialog, setExecutePendingDialog] = useState<{
    open: boolean;
    position: Position | null;
  }>({ open: false, position: null });
  const [closePositionDialog, setClosePositionDialog] = useState<{
    open: boolean;
    position: Position | null;
  }>({ open: false, position: null });
  const [editPositionDialog, setEditPositionDialog] = useState<{
    open: boolean;
    position: Position | null;
  }>({ open: false, position: null });
  const [editPortfolioDialog, setEditPortfolioDialog] = useState<{
    open: boolean;
    portfolio: Portfolio | null;
  }>({ open: false, portfolio: null });
  const [chartDialog, setChartDialog] = useState<{
    open: boolean;
    symbol: string | null;
    currentPrice: number;
    priceChangePercent: number;
  }>({ open: false, symbol: null, currentPrice: 0, priceChangePercent: 0 });
  const [isPortfolioShared, setIsPortfolioShared] = useState<{ [portfolioId: string]: boolean }>({});
  const [sharingLoading, setSharingLoading] = useState<{ [portfolioId: string]: boolean }>({});
  const [shareSettingsDialog, setShareSettingsDialog] = useState<{
    open: boolean;
    portfolioId: string | null;
  }>({ open: false, portfolioId: null });
  const [shareSettings, setShareSettings] = useState({
    showHoldings: true,
    showPerformance: true,
    showRules: true,
  });
  const [performanceDialog, setPerformanceDialog] = useState<{
    open: boolean;
    portfolioId: string | null;
    portfolioName: string;
  }>({ open: false, portfolioId: null, portfolioName: '' });

  useEffect(() => {
    loadPortfolios();
  }, []);

  // Check sharing status for all portfolios when user profile changes
  useEffect(() => {
    if (user && userProfile && portfolios.length > 0) {
      checkPortfolioSharingStatus();
    }
  }, [user, userProfile, portfolios]);

  const checkPortfolioSharingStatus = async () => {
    if (!user) return;
    
    const sharingStatus: { [portfolioId: string]: boolean } = {};
    for (const portfolio of portfolios) {
      const isShared = await SharedContentService.isContentShared(portfolio.id, 'portfolio');
      sharingStatus[portfolio.id] = isShared;
    }
    setIsPortfolioShared(sharingStatus);
  };

  const loadPortfolios = async () => {
    setLoading(true);
    try {
      const allPortfolios = await PortfolioService.getAllPortfolios();
      // Only show active portfolios, sorted: Real first, then Virtual
      const activePortfolios = allPortfolios
        .filter(p => p.isActive !== false)
        .sort((a, b) => {
          if (a.type !== b.type) return a.type === 'real' ? -1 : 1;
          return (a.createdAt?.getTime() || 0) - (b.createdAt?.getTime() || 0);
        });
      setPortfolios(activePortfolios);

      // Load open, pending, and closed positions for each portfolio
      const positionsMap: Record<string, Position[]> = {};
      const pendingMap: Record<string, Position[]> = {};
      const closedMap: Record<string, Position[]> = {};
      for (const portfolio of activePortfolios) {
        const [openPositions, pending, closed] = await Promise.all([
          PositionService.getOpenPositions(portfolio.id),
          PositionService.getPendingPositions(portfolio.id),
          PositionService.getClosedPositions(portfolio.id)
        ]);
        positionsMap[portfolio.id] = openPositions;
        pendingMap[portfolio.id] = pending;
        closedMap[portfolio.id] = closed;
      }
      setPositions(positionsMap);
      setPendingPositions(pendingMap);
      setClosedPositions(closedMap);
    } catch (error) {
      console.error('Error loading portfolios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePortfolio = async () => {
    if (!deleteDialog.portfolio) return;

    try {
      await PortfolioService.deletePortfolio(deleteDialog.portfolio.id);
      setDeleteDialog({ open: false, portfolio: null });
      loadPortfolios();
    } catch (error) {
      console.error('Error deleting portfolio:', error);
    }
  };

  const calculatePortfolioMetrics = (
    portfolio: Portfolio, 
    portfolioPositions: Position[] = [], 
    portfolioPendingPositions: Position[] = []
  ) => {
    // Current value of open positions
    const positionsValue = portfolioPositions.reduce(
      (sum, p) => sum + p.currentPrice * p.quantity,
      0
    );
    
    // Cost basis of open positions (what we paid)
    const positionsCost = portfolioPositions.reduce(
      (sum, p) => sum + p.entryPrice * p.quantity,
      0
    );
    
    // Cost of pending positions (at limit price)
    const pendingCost = portfolioPendingPositions.reduce(
      (sum, p) => sum + (p.limitPrice || p.entryPrice) * p.quantity,
      0
    );
    
    // Performance is based only on executed positions
    const positionsGainLoss = positionsValue - positionsCost;
    const positionsGainLossPercent = positionsCost > 0 ? (positionsGainLoss / positionsCost) * 100 : 0;
    
    // Available funds calculations
    // Available funds = Initial fund - open positions cost + losses (negative earnings)
    // We only subtract losses, we don't add gains to available funds
    const realizedLosses = Math.min(0, portfolio.totalEarnings || 0);
    const availableFunds = portfolio.initialFundValue - positionsCost + realizedLosses;
    // Available funds with pending = available funds - pending allocations
    const availableFundsPending = availableFunds - pendingCost;
    
    // Allocation percentages
    const allocatedPercent = (positionsCost / portfolio.initialFundValue) * 100;
    const allocatedPendingPercent = ((positionsCost + pendingCost) / portfolio.initialFundValue) * 100;

    return {
      totalValue: positionsValue, // Only executed positions value
      totalGainLoss: positionsGainLoss,
      totalGainLossPercent: positionsGainLossPercent,
      positionsValue,
      positionCount: portfolioPositions.length,
      allocatedPercent,
      allocatedPendingPercent,
      availableFunds,
      availableFundsPending,
      pendingCount: portfolioPendingPositions.length,
    };
  };

  const formatDateEST = (date: Date | undefined | null) => {
    if (!date) return '-';
    const estDate = toZonedTime(date, 'America/New_York');
    return format(estDate, 'MMM dd, yyyy h:mm a');
  };

  /**
   * Get the last activity date for a portfolio by looking at position entry/exit dates
   */
  const getLastActivityDate = (portfolioId: string): Date | null => {
    const openPos = positions[portfolioId] || [];
    const pendingPos = pendingPositions[portfolioId] || [];
    const closedPos = closedPositions[portfolioId] || [];

    let lastDate: Date | null = null;

    // Check all position entry dates and exit dates
    const allPositions = [...openPos, ...pendingPos, ...closedPos];

    for (const position of allPositions) {
      // Check entry date
      if (position.entryDate) {
        const entryDate = position.entryDate instanceof Date
          ? position.entryDate
          : new Date(position.entryDate);
        if (!lastDate || entryDate > lastDate) {
          lastDate = entryDate;
        }
      }

      // Check exit date (for closed positions)
      if (position.exitDate) {
        const exitDate = position.exitDate instanceof Date
          ? position.exitDate
          : new Date(position.exitDate);
        if (!lastDate || exitDate > lastDate) {
          lastDate = exitDate;
        }
      }
    }

    return lastDate;
  };

  const handleCreatePortfolio = () => {
    setCreateDialogOpen(true);
  };

  const handlePortfolioCreated = (portfolio: Portfolio) => {
    setCreateDialogOpen(false);
    loadPortfolios();
  };

  const handleShareToggle = async (portfolioId: string, portfolio: Portfolio) => {
    if (!user || !userProfile) {
      console.error('Cannot share: user or profile not loaded');
      return;
    }
    
    setSharingLoading(prev => ({ ...prev, [portfolioId]: true }));
    
    try {
      const currentlyShared = isPortfolioShared[portfolioId] || false;
      
      if (currentlyShared) {
        // Unshare the portfolio
        await SharedContentService.unsharePortfolio(portfolioId);
        setIsPortfolioShared(prev => ({ ...prev, [portfolioId]: false }));
      } else {
        // Show settings dialog for sharing
        setShareSettingsDialog({ open: true, portfolioId });
      }
    } catch (error: any) {
      console.error('Error toggling portfolio sharing:', error);
    } finally {
      setSharingLoading(prev => ({ ...prev, [portfolioId]: false }));
    }
  };

  const handleSharePortfolio = async (settings: {
    showHoldings: boolean;
    showPerformance: boolean;
    showRules: boolean;
  }) => {
    if (!shareSettingsDialog.portfolioId || !user || !userProfile) return;
    
    const portfolioId = shareSettingsDialog.portfolioId;
    const portfolio = portfolios.find(p => p.id === portfolioId);
    if (!portfolio) return;
    
    setSharingLoading(prev => ({ ...prev, [portfolioId]: true }));
    
    try {
      // Create a clean profile object to avoid undefined fields
      const cleanProfile: UserProfile = {
        uid: userProfile.uid,
        email: userProfile.email,
        isAdmin: userProfile.isAdmin,
        createdAt: userProfile.createdAt,
        updatedAt: userProfile.updatedAt,
      };
      
      // Only add optional fields if they exist and are not undefined
      if (userProfile.displayName !== undefined) cleanProfile.displayName = userProfile.displayName;
      if (userProfile.handle !== undefined) cleanProfile.handle = userProfile.handle;
      if (userProfile.bio !== undefined) cleanProfile.bio = userProfile.bio;
      if (userProfile.profilePicUrl !== undefined) cleanProfile.profilePicUrl = userProfile.profilePicUrl;
      if (userProfile.sharingPreferences !== undefined) cleanProfile.sharingPreferences = userProfile.sharingPreferences;
      
      await SharedContentService.sharePortfolio(portfolio, cleanProfile, settings);
      setIsPortfolioShared(prev => ({ ...prev, [portfolioId]: true }));
      setShareSettingsDialog({ open: false, portfolioId: null });
    } catch (error: any) {
      console.error('Error sharing portfolio:', error);
    } finally {
      setSharingLoading(prev => ({ ...prev, [portfolioId]: false }));
    }
  };

  const handleViewDetails = (portfolio: Portfolio) => {
    setSelectedPortfolio(portfolio);
    setDetailsOpen(true);
  };

  const handleExecutePendingPosition = (position: Position) => {
    setExecutePendingDialog({ open: true, position });
  };

  const handleToggleAutopilot = async (portfolio: Portfolio, enabled: boolean) => {
    try {
      await AutopilotService.toggleAutopilot(portfolio.id, enabled);
      loadPortfolios(); // Refresh to show updated state
    } catch (error) {
      console.error('Error toggling autopilot:', error);
    }
  };

  const handleToggleAlpacaLink = async (portfolio: Portfolio) => {
    try {
      if (portfolio.alpacaLinked) {
        if (!window.confirm('Unlink this portfolio from Alpaca paper trading? Future trades will use simulated execution only.')) return;
        await PortfolioService.unlinkFromAlpaca(portfolio.id);
      } else {
        await PortfolioService.linkToAlpaca(portfolio.id);
      }
      loadPortfolios();
      if (selectedPortfolio?.id === portfolio.id) {
        setSelectedPortfolio({ ...portfolio, alpacaLinked: !portfolio.alpacaLinked });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update Alpaca link';
      alert(message);
    }
  };

  const handlePendingPositionExecuted = () => {
    setExecutePendingDialog({ open: false, position: null });
    loadPortfolios();
  };

  const handleCancelPendingPosition = async (position: Position) => {
    if (window.confirm(`Are you sure you want to cancel this pending order for ${position.symbol}?`)) {
      try {
        await PositionService.cancelPendingPosition(position.id);
        loadPortfolios();
      } catch (error) {
        console.error('Error cancelling pending position:', error);
        alert('Failed to cancel pending position');
      }
    }
  };

  const handleClosePosition = (position: Position) => {
    setClosePositionDialog({ open: true, position });
  };

  const handlePositionClosed = async () => {
    setClosePositionDialog({ open: false, position: null });
    await loadPortfolios();
    // Also update the selected portfolio to refresh the details view
    if (selectedPortfolio) {
      const updatedPortfolio = await PortfolioService.getPortfolio(selectedPortfolio.id);
      if (updatedPortfolio) {
        setSelectedPortfolio(updatedPortfolio);
      }
    }
  };

  const handleEditPosition = (position: Position) => {
    setEditPositionDialog({ open: true, position });
  };

  const handlePositionUpdated = async () => {
    setEditPositionDialog({ open: false, position: null });
    await loadPortfolios();
  };

  // Mobile Portfolio View
  const renderMobileView = () => {
    // Default to first Real portfolio, then fall back to first portfolio
    const defaultPortfolio = portfolios.find(p => p.type === 'real') || portfolios[0];
    const selectedMobilePortfolio = portfolios.find(p => p.id === mobileSelectedPortfolioId) || defaultPortfolio;
    const portfolioPositions = selectedMobilePortfolio ? positions[selectedMobilePortfolio.id] || [] : [];
    const portfolioPendingPositions = selectedMobilePortfolio ? pendingPositions[selectedMobilePortfolio.id] || [] : [];
    const metrics = selectedMobilePortfolio
      ? calculatePortfolioMetrics(selectedMobilePortfolio, portfolioPositions, portfolioPendingPositions)
      : null;

    return (
      <Box sx={{ p: 1.5, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Portfolios</Typography>
          <IconButton color="primary" onClick={handleCreatePortfolio}>
            <Add />
          </IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : portfolios.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">No portfolios yet</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={handleCreatePortfolio} sx={{ mt: 2 }}>
              Create Your First Portfolio
            </Button>
          </Paper>
        ) : (
          <>
            {/* Portfolio Selector Tabs */}
            <Tabs
              value={mobileSelectedPortfolioId || defaultPortfolio?.id || ''}
              onChange={(_, value) => setMobileSelectedPortfolioId(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              {portfolios.map((p) => (
                <Tab
                  key={p.id}
                  value={p.id}
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2">{p.name}</Typography>
                      {p.type === 'real' && (
                        <Chip label="R" size="small" color="success" sx={{ height: 16, fontSize: '0.65rem' }} />
                      )}
                      {p.alpacaLinked && (
                        <Chip label="A" size="small" color="warning" variant="outlined" sx={{ height: 16, fontSize: '0.65rem' }} />
                      )}
                    </Stack>
                  }
                />
              ))}
            </Tabs>

            {/* Selected Portfolio Summary */}
            {selectedMobilePortfolio && metrics && (
              <>
                <MobilePortfolioSummary
                  portfolio={selectedMobilePortfolio}
                  metrics={metrics}
                  selected
                />

                {/* Portfolio Actions */}
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Timeline />}
                    onClick={() => setPerformanceDialog({
                      open: true,
                      portfolioId: selectedMobilePortfolio.id,
                      portfolioName: selectedMobilePortfolio.name
                    })}
                  >
                    Performance
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => setEditPortfolioDialog({ open: true, portfolio: selectedMobilePortfolio })}
                  >
                    Edit
                  </Button>
                </Stack>

                {/* Open Positions */}
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                  Open Positions ({portfolioPositions.length})
                </Typography>
                {portfolioPositions.length === 0 ? (
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      No open positions
                    </Typography>
                  </Paper>
                ) : (
                  portfolioPositions.map((position) => (
                    <MobilePositionCard
                      key={position.id}
                      position={position}
                      onViewChart={(symbol) => setChartDialog({
                        open: true,
                        symbol,
                        currentPrice: position.currentPrice,
                        priceChangePercent: ((position.currentPrice - position.entryPrice) / position.entryPrice) * 100
                      })}
                      onEdit={(pos) => setEditPositionDialog({ open: true, position: pos })}
                      onClose={(pos) => setClosePositionDialog({ open: true, position: pos })}
                    />
                  ))
                )}

                {/* Pending Positions */}
                {portfolioPendingPositions.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                      Pending Orders ({portfolioPendingPositions.length})
                    </Typography>
                    {portfolioPendingPositions.map((position) => (
                      <MobilePositionCard
                        key={position.id}
                        position={position}
                        onEdit={(pos) => setEditPositionDialog({ open: true, position: pos })}
                      />
                    ))}
                  </>
                )}
              </>
            )}
          </>
        )}
      </Box>
    );
  };

  // Desktop view uses original table-based layout
  if (isMobile) {
    return (
      <>
        {renderMobileView()}

        {/* Dialogs are shared between mobile and desktop */}
        <CreatePortfolioDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onCreated={handlePortfolioCreated}
        />
        <EditPortfolioDialog
          open={editPortfolioDialog.open}
          onClose={() => setEditPortfolioDialog({ open: false, portfolio: null })}
          portfolio={editPortfolioDialog.portfolio}
          onPortfolioUpdated={() => {
            loadPortfolios();
            setEditPortfolioDialog({ open: false, portfolio: null });
          }}
        />
        <ClosePositionDialog
          open={closePositionDialog.open}
          onClose={() => setClosePositionDialog({ open: false, position: null })}
          position={closePositionDialog.position}
          onPositionClosed={handlePositionClosed}
        />
        <EditPositionDialog
          open={editPositionDialog.open}
          onClose={() => setEditPositionDialog({ open: false, position: null })}
          position={editPositionDialog.position}
          onPositionUpdated={handlePositionUpdated}
        />
        <PerformanceChartDialog
          open={performanceDialog.open}
          onClose={() => setPerformanceDialog({ open: false, portfolioId: null, portfolioName: '' })}
          portfolioId={performanceDialog.portfolioId}
          portfolioName={performanceDialog.portfolioName}
        />
        <StockChartDialog
          chartDialog={chartDialog}
          onClose={() => setChartDialog({ ...chartDialog, open: false })}
        />
      </>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Portfolios</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreatePortfolio}
        >
          Create Portfolio
        </Button>
      </Box>

      {/* Group portfolios by type */}
      {(() => {
        const realPortfolios = portfolios.filter(p => p.type === 'real');
        const virtualPortfolios = portfolios.filter(p => p.type === 'virtual');
        
        return (
          <>
            {realPortfolios.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Real Portfolios
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Portfolio Name</TableCell>
                        <TableCell align="right">Fund Size</TableCell>
                        <TableCell align="right">Available Funds</TableCell>
                        <TableCell align="right">Available Funds (w/Pending)</TableCell>
                        <TableCell align="right">Total Value</TableCell>
                        <TableCell align="center">Unrealized G/L</TableCell>
                        <TableCell align="center">Total Earnings</TableCell>
                        <TableCell align="center">Positions</TableCell>
                        <TableCell>Last Updated</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {realPortfolios.map((portfolio) => {
                        const portfolioPositions = positions[portfolio.id] || [];
                        const portfolioPendingPositions = pendingPositions[portfolio.id] || [];
                        const metrics = calculatePortfolioMetrics(portfolio, portfolioPositions, portfolioPendingPositions);
                        const percentRemaining = (metrics.availableFunds / portfolio.initialFundValue) * 100;
                        const percentRemainingWithPending = (metrics.availableFundsPending / portfolio.initialFundValue) * 100;

                        return (
                          <TableRow key={portfolio.id}>
                            <TableCell>
                              <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                onClick={() => handleViewDetails(portfolio)}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': {
                                    color: 'primary.main',
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                {portfolio.name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(portfolio.initialFundValue)}
                            </TableCell>
                            <TableCell align="right">
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {formatCurrency(metrics.availableFunds)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  ({percentRemaining.toFixed(1)}%)
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {formatCurrency(metrics.availableFundsPending)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color={percentRemainingWithPending < 0 ? 'error' : 'textSecondary'}
                                >
                                  ({percentRemainingWithPending.toFixed(1)}%)
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">
                                {formatCurrency(metrics.totalValue)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Stack spacing={0.5} alignItems="center">
                                <Typography
                                  variant="body2"
                                  color={metrics.totalGainLoss >= 0 ? 'success.main' : 'error.main'}
                                  fontWeight="bold"
                                >
                                  {metrics.totalGainLoss >= 0 ? '+' : ''}
                                  {formatCurrency(metrics.totalGainLoss)}
                                </Typography>
                                <Chip
                                  label={formatPercent(metrics.totalGainLossPercent)}
                                  color={metrics.totalGainLoss >= 0 ? 'success' : 'error'}
                                  size="small"
                                  icon={metrics.totalGainLoss >= 0 ? <TrendingUp /> : <TrendingDown />}
                                />
                              </Stack>
                            </TableCell>
                            <TableCell align="center">
                              <Typography
                                variant="body2"
                                color={(portfolio.totalEarnings || 0) >= 0 ? 'success.main' : 'error.main'}
                                fontWeight="bold"
                              >
                                {(portfolio.totalEarnings || 0) >= 0 ? '+' : ''}
                                {formatCurrency(portfolio.totalEarnings || 0)}
                              </Typography>
                              {portfolio.initialFundValue > 0 && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color={(portfolio.totalEarnings || 0) >= 0 ? 'success.main' : 'error.main'}
                                >
                                  ({(((portfolio.totalEarnings || 0) / portfolio.initialFundValue) * 100).toFixed(1)}%)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${metrics.positionCount} / ${portfolio.maxPositions}`}
                                color={metrics.positionCount >= portfolio.maxPositions ? 'warning' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {formatDateEST(getLastActivityDate(portfolio.id) || portfolio.updatedAt)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewDetails(portfolio)}
                                title="View details"
                              >
                                <Visibility />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => setPerformanceDialog({
                                  open: true,
                                  portfolioId: portfolio.id,
                                  portfolioName: portfolio.name
                                })}
                                title="View performance"
                              >
                                <Timeline />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteDialog({ open: true, portfolio })}
                                title="Delete portfolio"
                                disabled={false}
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {virtualPortfolios.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Virtual Portfolios
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Portfolio Name</TableCell>
                        <TableCell align="center">Autopilot</TableCell>
                        <TableCell align="right">Fund Size</TableCell>
                        <TableCell align="right">Available Funds</TableCell>
                        <TableCell align="right">Available Funds (w/Pending)</TableCell>
                        <TableCell align="right">Total Value</TableCell>
                        <TableCell align="center">Unrealized G/L</TableCell>
                        <TableCell align="center">Total Earnings</TableCell>
                        <TableCell align="center">Positions</TableCell>
                        <TableCell>Last Updated</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {virtualPortfolios.map((portfolio) => {
                        const portfolioPositions = positions[portfolio.id] || [];
                        const portfolioPendingPositions = pendingPositions[portfolio.id] || [];
                        const metrics = calculatePortfolioMetrics(portfolio, portfolioPositions, portfolioPendingPositions);
                        const percentRemaining = (metrics.availableFunds / portfolio.initialFundValue) * 100;
                        const percentRemainingWithPending = (metrics.availableFundsPending / portfolio.initialFundValue) * 100;

                        return (
                          <TableRow key={portfolio.id}>
                            <TableCell>
                              <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                onClick={() => handleViewDetails(portfolio)}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': {
                                    color: 'primary.main',
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                {portfolio.name}
                                {portfolio.alpacaLinked && (
                                  <Chip label="Alpaca" size="small" color="warning" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={portfolio.autopilotEnabled ? 'Autopilot is active' : 'Enable autopilot to auto-execute recommendations'}>
                                <Switch
                                  checked={portfolio.autopilotEnabled || false}
                                  onChange={(e) => handleToggleAutopilot(portfolio, e.target.checked)}
                                  color="primary"
                                />
                              </Tooltip>
                              {portfolio.autopilotLastRun && (
                                <Typography variant="caption" display="block" color="textSecondary">
                                  Last run: {format(new Date(portfolio.autopilotLastRun), 'h:mm a')}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(portfolio.initialFundValue)}
                            </TableCell>
                            <TableCell align="right">
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {formatCurrency(metrics.availableFunds)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  ({percentRemaining.toFixed(1)}%)
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {formatCurrency(metrics.availableFundsPending)}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  color={percentRemainingWithPending < 0 ? 'error' : 'textSecondary'}
                                >
                                  ({percentRemainingWithPending.toFixed(1)}%)
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">
                                {formatCurrency(metrics.totalValue)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Stack spacing={0.5} alignItems="center">
                                <Typography
                                  variant="body2"
                                  color={metrics.totalGainLoss >= 0 ? 'success.main' : 'error.main'}
                                  fontWeight="bold"
                                >
                                  {metrics.totalGainLoss >= 0 ? '+' : ''}
                                  {formatCurrency(metrics.totalGainLoss)}
                                </Typography>
                                <Chip
                                  label={formatPercent(metrics.totalGainLossPercent)}
                                  color={metrics.totalGainLoss >= 0 ? 'success' : 'error'}
                                  size="small"
                                  icon={metrics.totalGainLoss >= 0 ? <TrendingUp /> : <TrendingDown />}
                                />
                              </Stack>
                            </TableCell>
                            <TableCell align="center">
                              <Typography
                                variant="body2"
                                color={(portfolio.totalEarnings || 0) >= 0 ? 'success.main' : 'error.main'}
                                fontWeight="bold"
                              >
                                {(portfolio.totalEarnings || 0) >= 0 ? '+' : ''}
                                {formatCurrency(portfolio.totalEarnings || 0)}
                              </Typography>
                              {portfolio.initialFundValue > 0 && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color={(portfolio.totalEarnings || 0) >= 0 ? 'success.main' : 'error.main'}
                                >
                                  ({(((portfolio.totalEarnings || 0) / portfolio.initialFundValue) * 100).toFixed(1)}%)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${metrics.positionCount} / ${portfolio.maxPositions}`}
                                color={metrics.positionCount >= portfolio.maxPositions ? 'warning' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {formatDateEST(getLastActivityDate(portfolio.id) || portfolio.updatedAt)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewDetails(portfolio)}
                                title="View details"
                              >
                                <Visibility />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => setPerformanceDialog({
                                  open: true,
                                  portfolioId: portfolio.id,
                                  portfolioName: portfolio.name
                                })}
                                title="View performance"
                              >
                                <Timeline />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteDialog({ open: true, portfolio })}
                                title="Delete portfolio"
                                disabled={false}
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        );
      })()}

      {portfolios.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <AccountBalance sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No portfolios yet
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Create your first portfolio to start trading
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreatePortfolio}>
            Create Portfolio
          </Button>
        </Box>
      )}

      {/* Create Portfolio Dialog */}
      <CreatePortfolioDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={handlePortfolioCreated}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, portfolio: null })}
      >
        <DialogTitle>Delete Portfolio</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteDialog.portfolio?.name}"? This will mark the 
            portfolio as inactive. Any open positions should be closed first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, portfolio: null })}>Cancel</Button>
          <Button onClick={handleDeletePortfolio} color="error" variant="contained">
            Delete Portfolio
          </Button>
        </DialogActions>
      </Dialog>

      {/* Portfolio Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              {selectedPortfolio?.name} - Portfolio Details
              <Typography variant="body2" color="textSecondary">
                Created: {formatDateEST(selectedPortfolio?.createdAt)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {userProfile?.isAdmin && selectedPortfolio?.type === 'virtual' && (
                <Button
                  variant={selectedPortfolio?.alpacaLinked ? "contained" : "outlined"}
                  startIcon={<AccountBalance />}
                  onClick={() => selectedPortfolio && handleToggleAlpacaLink(selectedPortfolio)}
                  color={selectedPortfolio?.alpacaLinked ? "warning" : "primary"}
                  size="small"
                >
                  {selectedPortfolio?.alpacaLinked ? 'Alpaca Linked' : 'Link Alpaca'}
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<RulesIcon />}
                onClick={() => {
                  setDetailsOpen(false);
                  navigate(`/rules?portfolio=${selectedPortfolio?.id}`);
                }}
                size="small"
              >
                Trading Rules
              </Button>
              <Button
                variant={selectedPortfolio && isPortfolioShared[selectedPortfolio.id] ? "contained" : "outlined"}
                startIcon={<ShareIcon />}
                onClick={() => selectedPortfolio && handleShareToggle(selectedPortfolio.id, selectedPortfolio)}
                disabled={selectedPortfolio ? sharingLoading[selectedPortfolio.id] || false : false}
                color={selectedPortfolio && isPortfolioShared[selectedPortfolio.id] ? "success" : "primary"}
                size="small"
              >
                {selectedPortfolio && sharingLoading[selectedPortfolio.id] ? 'Updating...' : selectedPortfolio && isPortfolioShared[selectedPortfolio.id] ? 'Shared' : 'Share'}
              </Button>
              <IconButton
                onClick={() => {
                  setEditPortfolioDialog({ open: true, portfolio: selectedPortfolio });
                }}
                size="small"
              >
                <Edit />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPortfolio && (
            <Box>
              <Stack direction="row" spacing={3} sx={{ mb: 3 }}>
                <Paper sx={{ p: 2, flex: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Fund Size
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(selectedPortfolio.initialFundValue)}
                  </Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Available Funds
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(
                      calculatePortfolioMetrics(
                        selectedPortfolio,
                        positions[selectedPortfolio.id] || []
                      ).availableFunds
                    )}
                  </Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Value
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(
                      calculatePortfolioMetrics(
                        selectedPortfolio,
                        positions[selectedPortfolio.id] || []
                      ).totalValue
                    )}
                  </Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Earnings
                  </Typography>
                  <Typography
                    variant="h6"
                    color={(selectedPortfolio.totalEarnings || 0) >= 0 ? 'success.main' : 'error.main'}
                  >
                    {(selectedPortfolio.totalEarnings || 0) >= 0 ? '+' : ''}
                    {formatCurrency(selectedPortfolio.totalEarnings || 0)}
                  </Typography>
                  {selectedPortfolio.initialFundValue > 0 && (
                    <Typography
                      variant="body2"
                      color={(selectedPortfolio.totalEarnings || 0) >= 0 ? 'success.main' : 'error.main'}
                    >
                      ({(((selectedPortfolio.totalEarnings || 0) / selectedPortfolio.initialFundValue) * 100).toFixed(1)}%)
                    </Typography>
                  )}
                </Paper>
              </Stack>

              <Typography variant="h6" sx={{ mb: 2 }}>
                Open Positions
              </Typography>
              {positions[selectedPortfolio.id]?.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Symbol</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Entry Price</TableCell>
                        <TableCell align="right">Current Price</TableCell>
                        <TableCell align="right">Stop Loss</TableCell>
                        <TableCell align="right">Target</TableCell>
                        <TableCell align="right">Value</TableCell>
                        <TableCell align="center">Gain/Loss</TableCell>
                        <TableCell>Entry Date</TableCell>
                        <TableCell align="center">Days Held</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {positions[selectedPortfolio.id].map((position) => (
                        <TableRow key={position.id}>
                          <TableCell>{position.symbol}</TableCell>
                          <TableCell align="right">{position.quantity}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(position.entryPrice)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(position.currentPrice)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(position.stopLoss)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(position.targetPrice)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(position.currentPrice * position.quantity)}
                          </TableCell>
                          <TableCell align="center">
                            <Stack spacing={0.5} alignItems="center">
                              <Typography
                                variant="body2"
                                color={
                                  position.unrealizedGainLoss >= 0 ? 'success.main' : 'error.main'
                                }
                              >
                                {position.unrealizedGainLoss >= 0 ? '+' : ''}
                                {formatCurrency(position.unrealizedGainLoss)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color={
                                  position.unrealizedGainLoss >= 0 ? 'success.main' : 'error.main'
                                }
                              >
                                {position.unrealizedGainLoss >= 0 ? '+' : ''}
                                {(
                                  ((position.currentPrice - position.entryPrice) /
                                    position.entryPrice) *
                                  100
                                ).toFixed(2)}
                                %
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>{formatDateEST(position.entryDate)}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={position.holdingDays}
                              color={position.holdingDays >= 28 ? 'warning' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => {
                                  setChartDialog({
                                    open: true,
                                    symbol: position.symbol,
                                    currentPrice: position.currentPrice,
                                    priceChangePercent: position.currentPrice && position.entryPrice
                                      ? ((position.currentPrice - position.entryPrice) / position.entryPrice) * 100
                                      : 0,
                                  });
                                }}
                                title="View chart"
                              >
                                <ShowChart />
                              </IconButton>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleEditPosition(position)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => handleClosePosition(position)}
                              >
                                Close
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No open positions in this portfolio
                </Typography>
              )}

              {/* Pending Positions Section */}
              {pendingPositions[selectedPortfolio.id]?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
                    Pending Positions
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Symbol</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Limit Price</TableCell>
                          <TableCell align="center">Current/Limit %</TableCell>
                          <TableCell align="right">Allocated Funds</TableCell>
                          <TableCell>Order Date</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendingPositions[selectedPortfolio.id].map((position) => {
                          const limitPrice = position.limitPrice || position.entryPrice;
                          const priceDiffPercent = position.currentPrice 
                            ? ((limitPrice - position.currentPrice) / limitPrice) * 100 
                            : 0;
                          
                          return (
                            <TableRow key={position.id}>
                              <TableCell>{position.symbol}</TableCell>
                              <TableCell align="right">{position.quantity}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(limitPrice)}
                              </TableCell>
                              <TableCell align="center">
                                {position.currentPrice ? (
                                  <Chip
                                    label={`${priceDiffPercent >= 0 ? '+' : ''}${priceDiffPercent.toFixed(2)}%`}
                                    size="small"
                                    color={priceDiffPercent >= 0 ? 'success' : 'error'}
                                  />
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(limitPrice * position.quantity)}
                              </TableCell>
                              <TableCell>{formatDateEST(position.entryDate)}</TableCell>
                              <TableCell align="center">
                                <Stack direction="row" spacing={1} justifyContent="center">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => {
                                      setChartDialog({
                                        open: true,
                                        symbol: position.symbol,
                                        currentPrice: position.currentPrice || 0,
                                        priceChangePercent: 0,
                                      });
                                    }}
                                    title="View chart"
                                  >
                                    <ShowChart />
                                  </IconButton>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleExecutePendingPosition(position)}
                                  >
                                    Execute
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleCancelPendingPosition(position)}
                                  >
                                    Cancel
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Transaction History Section */}
              {closedPositions[selectedPortfolio.id]?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
                    Transaction History
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Symbol</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Entry Price</TableCell>
                          <TableCell>Entry Date</TableCell>
                          <TableCell align="right">Exit Price</TableCell>
                          <TableCell>Exit Date</TableCell>
                          <TableCell align="right">Days Held</TableCell>
                          <TableCell align="center">Realized P&L</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {closedPositions[selectedPortfolio.id].map((position) => {
                          const daysHeld = position.entryDate && position.exitDate
                            ? differenceInDays(startOfDay(position.exitDate), startOfDay(position.entryDate))
                            : 0;
                          
                          return (
                            <TableRow key={position.id}>
                              <TableCell>{position.symbol}</TableCell>
                              <TableCell align="right">{position.quantity}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(position.entryPrice)}
                              </TableCell>
                              <TableCell>{formatDateEST(position.entryDate)}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(position.exitPrice || 0)}
                              </TableCell>
                              <TableCell>{formatDateEST(position.exitDate)}</TableCell>
                              <TableCell align="right">{daysHeld}</TableCell>
                              <TableCell align="center">
                                <Stack spacing={0.5} alignItems="center">
                                  <Typography
                                    variant="body2"
                                    color={
                                      (position.realizedGainLoss || 0) >= 0 ? 'success.main' : 'error.main'
                                    }
                                    fontWeight="bold"
                                  >
                                    {(position.realizedGainLoss || 0) >= 0 ? '+' : ''}
                                    {formatCurrency(position.realizedGainLoss || 0)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color={
                                      (position.realizedGainLoss || 0) >= 0 ? 'success.main' : 'error.main'
                                    }
                                  >
                                    {position.entryPrice > 0 ? (
                                      <>
                                        {(position.realizedGainLoss || 0) >= 0 ? '+' : ''}
                                        {(
                                          ((position.realizedGainLoss || 0) /
                                            (position.entryPrice * position.quantity)) *
                                          100
                                        ).toFixed(2)}
                                        %
                                      </>
                                    ) : (
                                      '-'
                                    )}
                                  </Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Execute Pending Position Dialog */}
      <ExecutePendingPositionDialog
        open={executePendingDialog.open}
        onClose={() => setExecutePendingDialog({ open: false, position: null })}
        position={executePendingDialog.position}
        onExecuted={handlePendingPositionExecuted}
      />

      {/* Close Position Dialog */}
      <ClosePositionDialog
        open={closePositionDialog.open}
        onClose={() => setClosePositionDialog({ open: false, position: null })}
        position={closePositionDialog.position}
        onPositionClosed={handlePositionClosed}
      />

      {/* Edit Position Dialog */}
      <EditPositionDialog
        open={editPositionDialog.open}
        onClose={() => setEditPositionDialog({ open: false, position: null })}
        position={editPositionDialog.position}
        onPositionUpdated={handlePositionUpdated}
      />
      {/* Edit Portfolio Dialog */}
      <EditPortfolioDialog
        open={editPortfolioDialog.open}
        onClose={() => setEditPortfolioDialog({ open: false, portfolio: null })}
        portfolio={editPortfolioDialog.portfolio}
        onPortfolioUpdated={() => {
          loadPortfolios();
          setEditPortfolioDialog({ open: false, portfolio: null });
        }}
      />

      {/* Stock Chart Dialog */}
      <StockChartDialog
        chartDialog={chartDialog}
        onClose={() => setChartDialog({ ...chartDialog, open: false })}
      />

      {/* Share Settings Dialog */}
      <Dialog
        open={shareSettingsDialog.open}
        onClose={() => setShareSettingsDialog({ open: false, portfolioId: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Share Portfolio Settings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose what information to share with the community:
          </Typography>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  defaultChecked
                  disabled
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Basic Information</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Portfolio name, type, and creation date (always shared)
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={shareSettings.showHoldings}
                  onChange={(e) => setShareSettings(prev => ({ ...prev, showHoldings: e.target.checked }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Holdings</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Current positions and allocations
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={shareSettings.showPerformance}
                  onChange={(e) => setShareSettings(prev => ({ ...prev, showPerformance: e.target.checked }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Performance</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Returns, gains/losses, and metrics
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={shareSettings.showRules}
                  onChange={(e) => setShareSettings(prev => ({ ...prev, showRules: e.target.checked }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Trading Rules</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Position sizing and risk management settings
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareSettingsDialog({ open: false, portfolioId: null })}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSharePortfolio(shareSettings)}
            variant="contained"
            color="primary"
          >
            Share Portfolio
          </Button>
        </DialogActions>
      </Dialog>

      {/* Performance Chart Dialog */}
      <PerformanceChartDialog
        open={performanceDialog.open}
        onClose={() => setPerformanceDialog({ open: false, portfolioId: null, portfolioName: '' })}
        portfolioId={performanceDialog.portfolioId}
        portfolioName={performanceDialog.portfolioName}
      />
    </Box>
  );
};

interface StockChartDialogProps {
  chartDialog: {
    open: boolean;
    symbol: string | null;
    currentPrice: number;
    priceChangePercent: number;
  };
  onClose: () => void;
}

function StockChartDialog({ chartDialog, onClose }: StockChartDialogProps) {
  const { data, loading, error, pivotPrice, currentVolume, volumeChgVs50Day } = useStockHistory(chartDialog.symbol || '', 30);

  return (
    <Dialog
      open={chartDialog.open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Historical Price Chart</Typography>
          <IconButton
            onClick={onClose}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        {!loading && !error && chartDialog.symbol && (
          <StockChart
            symbol={chartDialog.symbol}
            data={data}
            pivotPrice={pivotPrice}
            currentPrice={chartDialog.currentPrice}
            priceChangePercent={chartDialog.priceChangePercent}
            currentVolume={currentVolume}
            volumeChgVs50Day={volumeChgVs50Day}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PortfolioList;